import { useAuth } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, getExpenses, scanReceipt, getReceipts, connectGooglePhotos, getGooglePhotosAlbums, importGooglePhotosAlbum, getTripPhotos } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Camera, Upload, Loader2, Check, AlertCircle, Image as ImageIcon, Link as LinkIcon, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function TripBudget() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [googlePhotosConnected, setGooglePhotosConnected] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [importing, setImporting] = useState(false);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    loadData();
  }, [tripId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tripRes, expensesRes, receiptsRes] = await Promise.all([
        getTrip(tripId),
        getExpenses(tripId),
        getReceipts(tripId)
      ]);
      setTrip(tripRes.data);
      setExpenses(expensesRes.data);
      setReceipts(receiptsRes.data);
      
      // Try to load Google Photos data
      try {
        const photosRes = await getTripPhotos(tripId);
        setPhotos(photosRes.data);
        if (photosRes.data.length > 0) {
          setGooglePhotosConnected(true);
        }
      } catch (e) {
        // Not connected yet
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleReceiptUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress({ stage: 'upload', message: 'Upload de la photo...' });

    try {
      setUploadProgress({ stage: 'analyzing', message: 'Analyse du reçu avec IA...' });
      const res = await scanReceipt(tripId, file);
      
      setUploadProgress({ stage: 'success', message: 'Reçu analysé !', data: res.data });
      
      // Show success with extracted data
      toast.success(
        `Reçu ajouté: ${res.data.extracted_data.merchant_name || 'Dépense'} - ${res.data.extracted_data.amount} ${res.data.extracted_data.currency}` +
        (res.data.added_to_itinerary ? ' ✅ Ajouté à l\'itinéraire' : '')
      );
      
      // Reload data
      await loadData();
      
      setTimeout(() => {
        setUploadProgress(null);
        setUploading(false);
      }, 2000);
    } catch (error) {
      toast.error('Erreur lors de l\'analyse du reçu');
      setUploadProgress(null);
      setUploading(false);
    }
  };

  const handleConnectGooglePhotos = async () => {
    try {
      const res = await connectGooglePhotos(tripId);
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error('Erreur de connexion Google Photos');
    }
  };

  const handleLoadAlbums = async () => {
    try {
      const res = await getGooglePhotosAlbums(tripId);
      setAlbums(res.data.albums);
      toast.success(`${res.data.albums.length} albums trouvés`);
    } catch (error) {
      toast.error('Erreur de chargement des albums');
    }
  };

  const handleImportAlbum = async (albumId) => {
    setImporting(true);
    try {
      const res = await importGooglePhotosAlbum(tripId, albumId);
      toast.success(`${res.data.imported} photos importées, ${res.data.activities_created} activités ajoutées à l'itinéraire !`);
      await loadData();
    } catch (error) {
      toast.error('Erreur d\'import');
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <div className="h-48 bg-[#E2E8F0] rounded-2xl animate-pulse" />;

  const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
  const receiptExpenses = receipts.reduce((sum, exp) => sum + (exp.amount || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1E293B]">Budget - {trip?.name}</h1>
          <p className="text-sm text-[#64748B] mt-1">{trip?.destination}</p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-[#1E293B]">{totalExpenses.toFixed(2)} {trip?.currency || 'EUR'}</p>
          <p className="text-sm text-[#64748B]">{expenses.length} dépenses</p>
        </div>
      </div>

      {/* Upload Receipt Card */}
      <Card className="border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
              <Camera className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-[#1E293B]">Scanner un Reçu</h3>
              <p className="text-sm text-[#64748B]">Upload photo → IA extrait les infos → Ajout auto au budget ET à l'itinéraire</p>
            </div>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleReceiptUpload}
                className="hidden"
                disabled={uploading}
              />
              <button
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-2 rounded-xl font-medium hover:opacity-90 disabled:opacity-50"
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Analyse...' : 'Upload'}
              </button>
            </label>
          </div>

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="mt-4 p-4 bg-white rounded-xl border border-purple-200">
              <div className="flex items-center gap-3">
                {uploadProgress.stage === 'success' ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#1E293B]">{uploadProgress.message}</p>
                  {uploadProgress.data && (
                    <div className="mt-2 text-xs text-[#64748B] space-y-1">
                      <p>• {uploadProgress.data.extracted_data.merchant_name}</p>
                      <p>• {uploadProgress.data.extracted_data.amount} {uploadProgress.data.extracted_data.currency}</p>
                      <p>• {uploadProgress.data.extracted_data.date} à {uploadProgress.data.extracted_data.time}</p>
                      {uploadProgress.data.added_to_itinerary && (
                        <p className="text-green-600 font-medium">✅ Ajouté à l'itinéraire du {uploadProgress.data.extracted_data.date}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Photos Integration */}
      <Card className="border-2 border-dashed border-blue-300 bg-gradient-to-br from-blue-50 to-cyan-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
              <ImageIcon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-[#1E293B]">Google Photos</h3>
              <p className="text-sm text-[#64748B]">Importer album → Extraction GPS/temps → Enrichissement auto de l'itinéraire</p>
            </div>
            {!googlePhotosConnected ? (
              <button
                onClick={handleConnectGooglePhotos}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-2 rounded-xl font-medium hover:opacity-90"
              >
                <LinkIcon className="w-4 h-4" />
                Connecter
              </button>
            ) : (
              <button
                onClick={handleLoadAlbums}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-2 rounded-xl font-medium hover:opacity-90"
              >
                <Download className="w-4 h-4" />
                Charger Albums
              </button>
            )}
          </div>

          {/* Albums List */}
          {albums.length > 0 && (
            <div className="mt-4 space-y-2">
              {albums.slice(0, 5).map(album => (
                <div key={album.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-blue-200">
                  <div>
                    <p className="font-medium text-sm text-[#1E293B]">{album.title}</p>
                    <p className="text-xs text-[#64748B]">{album.mediaItemsCount} photos</p>
                  </div>
                  <button
                    onClick={() => handleImportAlbum(album.id)}
                    disabled={importing}
                    className="text-sm bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  >
                    {importing ? 'Import...' : 'Importer'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Photos Count */}
          {photos.length > 0 && (
            <div className="mt-4 p-3 bg-white rounded-xl border border-blue-200">
              <p className="text-sm text-[#64748B]">📸 {photos.length} photos importées</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receipts List */}
      {receipts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-[#1E293B] mb-3">Reçus Scannés ({receipts.length})</h2>
          <div className="grid gap-3">
            {receipts.map(receipt => (
              <Card key={receipt.expense_id} className="border border-[#E2E8F0]/40">
                <CardContent className="p-4 flex items-start gap-4">
                  {receipt.receipt_url && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${receipt.receipt_url}`}
                      alt="Receipt"
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-[#1E293B]">{receipt.title}</p>
                        <p className="text-sm text-[#64748B]">{receipt.date} à {receipt.time}</p>
                        {receipt.merchant_address && (
                          <p className="text-xs text-[#64748B] mt-1">📍 {receipt.merchant_address}</p>
                        )}
                      </div>
                      <p className="text-lg font-bold text-[#1E293B]">{receipt.amount} {receipt.currency}</p>
                    </div>
                    {receipt.added_to_itinerary && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                        <Check className="w-3 h-3" />
                        Ajouté à l'itinéraire
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* All Expenses */}
      <div>
        <h2 className="text-lg font-semibold text-[#1E293B] mb-3">Toutes les Dépenses ({expenses.length})</h2>
        <div className="grid gap-2">
          {expenses.map(exp => (
            <Card key={exp.expense_id} className="border border-[#E2E8F0]/40">
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm text-[#1E293B]">{exp.title}</p>
                  <p className="text-xs text-[#64748B]">{exp.category} • {exp.date}</p>
                </div>
                <p className="font-bold text-[#1E293B]">{exp.amount} {exp.currency}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
